import { nonNullishOrThrow } from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';
import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { getPersonalItemOutcome } from './getPersonalItemOutcome';
import { mapSonarV3PricingOptions } from './sonarV3PricingOptionsMapper';
import { mapSonarV3Upgrades } from './sonarV3UpgradesMapper';

import type { SkyscannerSonarV3Response } from '@skyscanner-internal/sonar-web-http-client';
import type { SonarV3PayloadResponseSuccess } from '@web-platform/shared-universal-types/src/network/types';
import type { SonarV3AlphaPayloadResponseSuccess } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { ItineraryByIdBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

type PricingOption = SkyscannerSonarV3Response.PricingOption;
type PricingOptionIdsByAttribute = SkyscannerSonarV3Response.PricingOptionIdsByAttribute;

// Tracks itinerary IDs we've already warned about for a missing token on the
// Booking Panel, so the observability log fires at most once per itinerary
// regardless of how many times Sonar is polled for the same Booking Panel view.
const warnedItineraryIds = new Set<string>();

export const getBasePricingOptions = (
  pricingOptions: PricingOption[],
  pricingOptionIdsByAttribute?: PricingOptionIdsByAttribute[],
): PricingOption[] => {
  if (!pricingOptionIdsByAttribute?.length) {
    return pricingOptions;
  }

  const baseOptionsAttributeLookup = pricingOptionIdsByAttribute.find(
    ({ attributes }) => attributes?.length === 0,
  );

  const basePricingOptionIds = nonNullishOrThrow(
    baseOptionsAttributeLookup?.pricingOptionIds,
    'baseOptionsAttributeLookup.pricingOptionIds',
  );

  return basePricingOptionIds
    .map((id: string) => pricingOptions.find((po: PricingOption) => po.id === id))
    .filter((po: Maybe<PricingOption>): po is PricingOption => !!po);
};

const stablePricingOptions = (
  newOptions: ReturnType<typeof mapSonarV3PricingOptions>,
  oldOptions: ItineraryByIdBookingPanel[string]['pricing_options'] | undefined,
): ItineraryByIdBookingPanel[string]['pricing_options'] => {
  if (
    !oldOptions ||
    newOptions.length !== oldOptions.length ||
    newOptions.some(
      (opt, i) =>
        opt.id !== oldOptions[i].id ||
        opt.price?.amount !== oldOptions[i].price?.amount ||
        opt.price?.update_status !== oldOptions[i].price?.update_status ||
        opt.unpriced_type !== oldOptions[i].unpriced_type,
    )
  ) {
    return newOptions;
  }
  return oldOptions;
};

export const mapSonarV3Itineraries = (
  state: ItineraryByIdBookingPanel,
  response: SonarV3PayloadResponseSuccess | SonarV3AlphaPayloadResponseSuccess,
  isItineraryPathResult: boolean,
): ItineraryByIdBookingPanel => {
  try {
    const itinerary = nonNullishOrThrow(response.body.itinerary, 'itinerary');
    const itineraryId = nonNullishOrThrow(itinerary.id, 'itinerary.id');
    const { token } = itinerary;
    const pricingOptions = nonNullishOrThrow(itinerary.pricingOptions, 'itinerary.pricingOptions');
    const { pricingOptionIdsByAttribute } = itinerary;

    if (isItineraryPathResult && !token && !warnedItineraryIds.has(itineraryId)) {
      warnedItineraryIds.add(itineraryId);
      logWarn({
        component: 'mapSonarV3Itineraries',
        message: 'Itinerary token is missing when isItineraryPathResult is true',
        itineraryId,
      });
    }

    const itineraryTokenOrId = isItineraryPathResult && token ? token : itineraryId;

    const basePricingOptions = getBasePricingOptions(pricingOptions, pricingOptionIdsByAttribute);

    const personalItemOutcome = getPersonalItemOutcome(itinerary);

    return {
      ...state,
      [itineraryTokenOrId]: {
        ...state[itineraryTokenOrId],
        id: itineraryId,
        token,
        pricing_options: stablePricingOptions(
          mapSonarV3PricingOptions(basePricingOptions, personalItemOutcome),
          state[itineraryTokenOrId]?.pricing_options,
        ),
        valid: true,
        upgrades: mapSonarV3Upgrades(
          pricingOptions,
          pricingOptionIdsByAttribute,
          personalItemOutcome,
        ),
        error: undefined,
        ...('legs' in itinerary && itinerary.legs
          ? {
              leg_ids: itinerary.legs.map(({ id }) => nonNullishOrThrow(id, 'leg.id')),
            }
          : {}),
      },
    };
  } catch (err) {
    logError({
      component: 'mapSonarV3Itineraries',
      message: 'Error reducing sonar V3 itinerary',
      error: err,
    });
    return state;
  }
};
